import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Calculator, 
  LogOut, 
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Laptop
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

const AdminEquipment = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [equipment, setEquipment] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "laptop",
    monthly_cost: 0,
    purchase_cost: 0,
    depreciation_months: 36,
    description: "",
    active: true
  });

  useEffect(() => {
    loadEquipment();
  }, []);

  const loadEquipment = async () => {
    try {
      const response = await axios.get(`${API}/equipment?active=true`, { headers: getAuthHeaders() });
      setEquipment(response.data);
    } catch (error) {
      toast.error("Erro ao carregar equipamentos");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const openNewDialog = () => {
    setEditingEquipment(null);
    setFormData({
      name: "",
      category: "laptop",
      monthly_cost: 0,
      purchase_cost: 0,
      depreciation_months: 36,
      description: "",
      active: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (equip) => {
    setEditingEquipment(equip);
    setFormData({
      name: equip.name,
      category: equip.category,
      monthly_cost: equip.monthly_cost,
      purchase_cost: equip.purchase_cost,
      depreciation_months: equip.depreciation_months,
      description: equip.description || "",
      active: equip.active
    });
    setIsDialogOpen(true);
  };

  const calculateMonthly = () => {
    if (formData.purchase_cost && formData.depreciation_months) {
      const monthly = formData.purchase_cost / formData.depreciation_months;
      setFormData({ ...formData, monthly_cost: parseFloat(monthly.toFixed(2)) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        monthly_cost: parseFloat(formData.monthly_cost) || 0,
        purchase_cost: parseFloat(formData.purchase_cost) || 0,
        depreciation_months: parseInt(formData.depreciation_months) || 36
      };

      if (editingEquipment) {
        await axios.put(`${API}/equipment/${editingEquipment.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Equipamento atualizado com sucesso!");
      } else {
        await axios.post(`${API}/equipment`, payload, { headers: getAuthHeaders() });
        toast.success("Equipamento criado com sucesso!");
      }
      setIsDialogOpen(false);
      loadEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar equipamento");
    }
  };

  const handleDelete = async (equipId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este equipamento?")) return;
    try {
      await axios.delete(`${API}/equipment/${equipId}`, { headers: getAuthHeaders() });
      toast.success("Equipamento eliminado");
      loadEquipment();
    } catch (error) {
      toast.error("Erro ao eliminar equipamento");
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      laptop: "Portátil",
      desktop: "Desktop",
      monitor: "Monitor",
      other: "Outro"
    };
    return labels[category] || category;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-equipment-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Equipamentos</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Back & Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground">Equipamentos</h1>
              <p className="text-muted-foreground">Gerir computadores e equipamentos</p>
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                onClick={openNewDialog}
                data-testid="add-equipment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Equipamento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-xl">
                  {editingEquipment ? "Editar Equipamento" : "Novo Equipamento"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder='Ex: MacBook Pro 14"'
                    data-testid="name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="laptop">Portátil</SelectItem>
                        <SelectItem value="desktop">Desktop</SelectItem>
                        <SelectItem value="monitor">Monitor</SelectItem>
                        <SelectItem value="other">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Amortização (meses)</Label>
                    <Input
                      type="number"
                      value={formData.depreciation_months}
                      onChange={(e) => setFormData({ ...formData, depreciation_months: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Custo Aquisição (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.purchase_cost}
                      onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })}
                      onBlur={calculateMonthly}
                      className="font-mono"
                      data-testid="purchase-cost-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Custo Mensal (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.monthly_cost}
                      onChange={(e) => setFormData({ ...formData, monthly_cost: e.target.value })}
                      className="font-mono"
                      data-testid="monthly-cost-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição do equipamento"
                    data-testid="description-input"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Label>Ativo</Label>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                    data-testid="active-switch"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-btn">
                    {editingEquipment ? "Guardar Alterações" : "Criar Equipamento"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Equipment List */}
        <Card className="bg-white border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : equipment.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Laptop className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhum equipamento configurado</p>
                <p className="text-sm text-muted-foreground">Clique em "Novo Equipamento" ou carregue os dados iniciais</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Categoria</TableHead>
                    <TableHead className="font-semibold">Aquisição</TableHead>
                    <TableHead className="font-semibold">Mensal</TableHead>
                    <TableHead className="font-semibold">Amortização</TableHead>
                    <TableHead className="font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {equipment.map((equip) => (
                    <TableRow key={equip.id} data-testid={`equipment-row-${equip.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{equip.name}</p>
                          {equip.description && (
                            <p className="text-xs text-muted-foreground">{equip.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {getCategoryLabel(equip.category)}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono">{formatCurrency(equip.purchase_cost)}</TableCell>
                      <TableCell className="font-mono font-medium text-primary">{formatCurrency(equip.monthly_cost)}</TableCell>
                      <TableCell className="text-muted-foreground">{equip.depreciation_months} meses</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(equip)}
                            data-testid={`edit-btn-${equip.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(equip.id)}
                            data-testid={`delete-btn-${equip.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminEquipment;
